import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { login } from '../services/AuthService';
import { storeAccessToken } from '../utils/AuthTokenUtil';

interface LoginModalProps {
  show: boolean;
  onHide: () => void;
  onLoginSuccess: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ show, onHide, onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const usernameInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const clearLoginFields = useCallback(() => {
    setUsername('');
    setPassword('');
    if (usernameInputRef.current) {
      usernameInputRef.current.value = '';
    }
    if (passwordInputRef.current) {
      passwordInputRef.current.value = '';
    }
  }, []);

  const handleUsernameChange = (value: string) => {
    if (value.trim().toLowerCase().startsWith('jdbc:')) {
      setUsername('');
      if (usernameInputRef.current) {
        usernameInputRef.current.value = '';
      }
      return;
    }
    setUsername(value);
  };

  useEffect(() => {
    if (!show) {
      return;
    }

    clearLoginFields();
    setError('');
    setSubmitting(false);

    const timerIds = [0, 100, 500].map((delay) => window.setTimeout(() => {
      clearLoginFields();
      if (usernameInputRef.current) {
        usernameInputRef.current.focus();
      }
    }, delay));

    return () => timerIds.forEach(window.clearTimeout);
  }, [show, clearLoginFields]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const normalizedUsername = username.trim();
      if (normalizedUsername.toLowerCase().startsWith('jdbc:')) {
        clearLoginFields();
        setError('사용자명을 다시 입력해주세요.');
        return;
      }

      const data = await login(normalizedUsername, password);
      storeAccessToken(data.accessToken);
      onLoginSuccess();
      onHide();
    } catch (err) {
      setError('로그인에 실패했습니다. 계정 정보와 백엔드 실행 상태를 확인하세요.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} onEntered={() => usernameInputRef.current?.focus()} centered>
      <Modal.Header closeButton>
        <Modal.Title>로그인</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleLogin} autoComplete="off">
          <Form.Group className="mb-3">
            <Form.Label>사용자명</Form.Label>
            <Form.Control 
              ref={usernameInputRef}
              name="borine-login-username"
              type="text" 
              value={username} 
              onChange={(e) => handleUsernameChange(e.target.value)}
              onFocus={(e) => handleUsernameChange(e.currentTarget.value)}
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
              required 
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>비밀번호</Form.Label>
            <Form.Control 
              ref={passwordInputRef}
              name="borine-login-password"
              type="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              autoComplete="new-password"
              data-lpignore="true"
              data-1p-ignore="true"
              required 
            />
          </Form.Group>
          {error && <p className="text-danger">{error}</p>}
          <Button variant="dark" type="submit" className="w-100" disabled={submitting}>
            {submitting ? '로그인 중...' : '로그인'}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default LoginModal;
